import { BUDGET, type RoundSetup, type Theme } from '../game/themes';
import { basePath, type ThemeVisual } from '../game/visual';
import type { T } from '../i18n/strings';
import { Button, PLAY_HOST, useCountdown, VoteBars } from '../components/ui';
import type { Tally } from '../stream/useVote';
import { VOTE_COLORS } from '../theme';

interface Props {
  t: T;
  round: RoundSetup;
  visual: ThemeVisual | null;
  packName: string;
  opener: string;
  onSkip: () => void;
  onStart: () => void;
  onMenu: () => void;
  showLink: boolean;
}

/// "Build the best…" card: the theme's picture, its title and Start.
export function ThemeScreen({ t, round, visual, packName, opener, onSkip, onStart, onMenu, showLink }: Props) {
  return (
    <div className="theme-screen">
      <header className="stage-top">
        <button className="icon-btn" onClick={onMenu} aria-label={t.t('menu')}>
          ☰
        </button>
        <div />
        <div />
      </header>
      <div className="theme-body">
        <div className="theme-art">
          {visual ? <img src={basePath(visual)} alt="" /> : <div className="theme-art-empty">$20</div>}
        </div>
        <div className="theme-info">
          <div className="theme-eyebrow">{t.a('twentyChallengeBuildTheBest')}</div>
          <h1 className="theme-title">{round.theme.title}</h1>
          <div className="chip">{packName}</div>
          <p className="theme-meta">
            {t.a('twentyChallengeThemeInfo', { count: round.items.length, budget: BUDGET, name: opener })}
          </p>
          <div className="theme-actions">
            <Button kind="secondary" onClick={onSkip}>
              {t.a('skip')}
            </Button>
            <Button onClick={onStart} className="big">
              {t.a('twentyChallengeStartGame')}
            </Button>
          </div>
        </div>
      </div>
      {showLink && <div className="watermark">{PLAY_HOST}</div>}
    </div>
  );
}

interface VoteProps {
  t: T;
  candidates: Theme[];
  visuals: (ThemeVisual | null)[];
  tally: Tally;
  seconds: number;
  onPick: (index: number) => void;
  onMenu: () => void;
  onClose: () => void;
  showLink: boolean;
}

/// Chat picks one of three themes: "1", "2" or "3" in chat.
export function ThemeVote({ t, candidates, visuals, tally, seconds, onPick, onMenu, onClose, showLink }: VoteProps) {
  const left = useCountdown(true, seconds, onClose);
  const max = Math.max(1, ...tally.counts);
  return (
    <div className="theme-vote">
      <header className="stage-top">
        <button className="icon-btn" onClick={onMenu} aria-label={t.t('menu')}>
          ☰
        </button>
        <div className="stage-title">{t.t('themeVote')}</div>
        <div className="countdown">{left}s</div>
      </header>
      <p className="vote-how">{t.t('themeVoteHow')}</p>
      <div className="vote-cards">
        {candidates.map((c, i) => (
          <button className="vote-card" key={c.id} onClick={() => onPick(i)}>
            <span className="vote-num" style={{ background: VOTE_COLORS[i] }}>
              {i + 1}
            </span>
            <span className="vote-art">
              {visuals[i] ? <img src={basePath(visuals[i]!)} alt="" /> : <span className="theme-art-empty">$20</span>}
            </span>
            <span className="vote-title">{c.title}</span>
            <span className="vote-meter">
              <span style={{ width: `${(tally.counts[i] / max) * 100}%`, background: VOTE_COLORS[i] }} />
            </span>
            <span className="vote-count">{t.t('votes', { count: tally.counts[i] ?? 0 })}</span>
          </button>
        ))}
      </div>
      <div className="vote-foot">
        <span className="vote-last">{tally.last ? `${tally.last} ✓` : ''}</span>
        <Button kind="secondary" onClick={onClose}>
          {t.t('closeVote')}
        </Button>
      </div>
      {showLink && <div className="watermark">{PLAY_HOST}</div>}
    </div>
  );
}

export { VoteBars };
